import { formatMoney, formatDate } from '../lib/format';
import {
  useTransactionList,
  TransactionListToolbar,
  StatusBadge,
  Pager,
  ListStates,
  SortableTh,
  type StatusOption,
} from './TransactionList';
import {
  CustomerFilter,
  DateRangeFilter,
} from './TransactionFilters';
import { ViewHeader } from './DashboardLayout';
import { useConnection, stripeInvoiceUrl, hubspotContactUrl } from './links';
import { ExtLink } from './ExtLink';
import type { InvoiceListEntry } from './types';

/**
 * Dashboard → Invoices (M19 T2 + T3).
 *
 * Billing-intent ledger: one row per Stripe Invoice. Filterable by status
 * ("open" = awaiting payment, "uncollectible" = bad debt review), the
 * universal Customer typeahead, and a date-range preset. The invoice id links
 * to the Stripe dashboard invoice (the customer-facing hosted invoice page is
 * reachable from there — previously a second adjacent icon link lived next to
 * the id, which read as a duplicate; it's been removed).
 */

const STATUS_OPTIONS: StatusOption[] = [
  { value: 'draft', label: 'Draft', tone: 'neutral' },
  { value: 'open', label: 'Open', tone: 'brand' },
  { value: 'paid', label: 'Paid', tone: 'success' },
  { value: 'void', label: 'Void', tone: 'neutral' },
  { value: 'uncollectible', label: 'Uncollectible', tone: 'danger' },
];

export function Invoices() {
  const list = useTransactionList<InvoiceListEntry>('admin/invoices', 'status');
  const {
    data,
    loading,
    error,
    refetch,
    status,
    setStatus,
    customer,
    setCustomer,
    dateRange,
    setDateRange,
    sort,
    dir,
    toggleSort,
    setOffset,
  } = list;

  return (
    <>
      <ViewHeader
        title="Invoices"
        subtitle="Stripe Invoices — billing intent, dunning, and bad debt."
      />
      <TransactionListToolbar
        status={status}
        setStatus={setStatus}
        statusOptions={STATUS_OPTIONS}
        statusLabel="Status"
      />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <CustomerFilter
          selected={customer}
          selectedLabel={null}
          onSelect={(id) => {
            setCustomer(id);
            setOffset(0);
          }}
          onClear={() => setCustomer(null)}
        />
        <DateRangeFilter value={dateRange} onChange={setDateRange} />
      </div>

      {(loading || error || (data && data.items.length === 0)) && (
        <ListStates
          loading={loading}
          error={error}
          empty={!!data && data.items.length === 0}
          onRetry={refetch}
        />
      )}

      {data && data.items.length > 0 && (
        <>
          <div className="overflow-x-auto rounded-xl border border-ink-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-ink-50 text-left text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  <th className="px-4 py-2 font-medium">Invoice</th>
                  <th className="px-4 py-2 font-medium">Customer</th>
                  <SortableTh
                    label="Amount"
                    sortKey="amount"
                    activeSort={sort}
                    sortDir={dir}
                    onSort={toggleSort}
                    align="right"
                  />
                  <SortableTh
                    label="Status"
                    sortKey="status"
                    activeSort={sort}
                    sortDir={dir}
                    onSort={toggleSort}
                  />
                  <SortableTh
                    label="Paid"
                    sortKey="paid"
                    activeSort={sort}
                    sortDir={dir}
                    onSort={toggleSort}
                  />
                  <SortableTh
                    label="Created"
                    sortKey="created"
                    activeSort={sort}
                    sortDir={dir}
                    onSort={toggleSort}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {data.items.map((inv) => (
                  <InvoiceRow key={inv.id} invoice={inv} />
                ))}
              </tbody>
            </table>
          </div>
          <Pager
            total={data.total}
            offset={data.offset}
            pageSize={data.limit}
            setOffset={setOffset}
          />
        </>
      )}
    </>
  );
}

function InvoiceRow({ invoice }: { invoice: InvoiceListEntry; }) {
  const { portalId, stripeAccountId } = useConnection();
  const stripeUrl = stripeInvoiceUrl(stripeAccountId, invoice.id);
  const hsUrl = hubspotContactUrl(portalId, invoice.customerEmail ?? '');
  return (
    <tr className="hover:bg-ink-50/50">
      <td className="px-4 py-2">
        {stripeUrl ? (
          <ExtLink href={stripeUrl} label={invoice.id} title="View in Stripe" />
        ) : (
          <span className="text-xs text-ink-600">{invoice.id}</span>
        )}
      </td>
      <td className="px-4 py-2">
        <div className="flex flex-col">
          <span className="text-ink-800">{invoice.customerName ?? '—'}</span>
          {invoice.customerEmail && (
            <span className="text-xs text-ink-400">
              {hsUrl ? (
                <ExtLink href={hsUrl} label={invoice.customerEmail} title="View in HubSpot" />
              ) : (
                invoice.customerEmail
              )}
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-2 whitespace-nowrap text-right text-ink-800">
        {formatMoney(invoice.amountMinor, invoice.currency)}
      </td>
      <td className="px-4 py-2">
        <StatusBadge status={invoice.status} options={STATUS_OPTIONS} />
      </td>
      <td className="px-4 py-2 whitespace-nowrap text-xs text-ink-500">
        {invoice.paidAt ? formatDate(invoice.paidAt) : '—'}
      </td>
      <td className="px-4 py-2 whitespace-nowrap text-xs text-ink-500" title={formatDate(invoice.createdAt)}>
        {formatDate(invoice.createdAt)}
      </td>
    </tr>
  );
}
